import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from '@heroicons/react/24/outline';

import { ProtectedPdfViewer } from './protected-pdf-viewer';

type MagazineViewerModalProps = {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  magazineId: number;
};

export const MagazineViewerModal: React.FC<MagazineViewerModalProps> = ({
  isOpen,
  onClose,
  name,
  magazineId,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      panelRef.current?.requestFullscreen?.().catch(() => undefined);
    } else {
      document.exitFullscreen?.().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    const handleChange = () => setIsFullscreen(Boolean(document.fullscreenElement));

    document.addEventListener('fullscreenchange', handleChange);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, []);

  // Leave fullscreen when the modal closes.
  useEffect(() => {
    if (!isOpen && document.fullscreenElement) document.exitFullscreen?.();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const blockShortcuts = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if ((event.ctrlKey || event.metaKey) && (key === 'p' || key === 's')) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener('keydown', blockShortcuts, true);
    return () => window.removeEventListener('keydown', blockShortcuts, true);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => undefined}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-70" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={`w-full transform overflow-hidden bg-white text-left align-middle shadow-xl transition-all ${
                  isFullscreen
                    ? 'h-screen max-w-none rounded-none'
                    : 'h-[90vh] max-w-5xl rounded-2xl'
                }`}
              >
                <div ref={panelRef} className="flex h-full w-full flex-col bg-white">
                  <div className="flex items-center justify-between border-b bg-white px-4 py-3">
                    <h3 className="truncate pr-4 text-base font-semibold text-gray-800">{name}</h3>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={toggleFullscreen}
                        className="rounded p-1 text-gray-600 hover:bg-gray-100"
                        title={isFullscreen ? 'Exit full screen' : 'View full screen'}
                      >
                        {isFullscreen ? (
                          <ArrowsPointingInIcon className="h-5 w-5" />
                        ) : (
                          <ArrowsPointingOutIcon className="h-5 w-5" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={onClose}
                        className="rounded p-1 text-gray-600 hover:bg-gray-100"
                        title="Close"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  <div className="min-h-0 flex-1 bg-gray-100">
                    <ProtectedPdfViewer magazineId={magazineId} />
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
